import React from 'react';
import { AdminSidebarComponentProps } from '../../JsonComponent.types';
import { useCreateNewJsonComponent } from '../../hooks/useJsonBlobStorageUtils';
import { SimpleGrabHandle } from '../SimpleGrabHandle';
import { TextV2 } from '@withjoy/joykit';
import { Experiences } from '@withjoy/joykit/icons';
import { LayoutAddComponentListItem } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutAddComponentListItem';
import { DefaultThingsToDoConfig } from './DefaultThingsToDo.config';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { JsonDataContainerFragment } from '@graphql/generated';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { CUSTOM_PAGE_ACTIONS } from '../../../CustomComponents.constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useWebsiteEditorThingsToDoTelemetry } from './DefaultThingsToDo.telemetry';
import { useTranslation } from '@shared/core';

// Tip:
// This is the grab handle for your component in the sidebar
// while re-ordering components.
export const DefaultThingsToDoGrabber = (props: AdminSidebarComponentProps) => {
  const { t } = useTranslation('websiteEditor');
  const { recommendedThingsToDo } = t('customPage', 'thingsToDo');
  return (
    <SimpleGrabHandle
      title={recommendedThingsToDo()}
      icon={<Experiences size={32} />}
      disableEdit={true}
      {...props}
    ></SimpleGrabHandle>
  );
};

// Tip:
// This is the button that shows in the "Add Content" page
export const DefaultThingsToDoAdd = (props: AdminSidebarComponentProps) => {
  const { createNewJsonComponent } = useCreateNewJsonComponent(props.eventHandle);
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { setRefetchQueries } = useCustomComponentStateContext<JsonDataContainerFragment>();
  const { replaceAndNavigateToScreen } = useNavigationPaneContext();
  const telemetry = useWebsiteEditorThingsToDoTelemetry();
  const { t } = useTranslation('websiteEditor');
  const { recommendedThingsToDo } = t('customPage', 'thingsToDo');

  const handleClick = useEventCallback(() => {
    createNewJsonComponent({
      schemaURL: DefaultThingsToDoConfig.schemaUrl,
      type: DefaultThingsToDoConfig.componentType,
      initialData: { name: 'Default' },
      containerId: props.containerId,
      sortOrder: props.componentData?.sortOrder,
    }).then(result => {
      if (result?.containerData?.data) {
        telemetry.saveDefaultThingsToDoClicked(props.pageSlug);
        // Request a refetch of the components list to update the left panel
        setRefetchQueries(['PageContainerBySlug']);

        // Then update the preview
        updateWebsitePreview();

        // Finally replace and navigate to ensure proper left panel update
        replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
      }
    });
  });

  return (
    <LayoutAddComponentListItem onClick={handleClick}>
      <Experiences size={48} />
      <TextV2
        typographyVariant="hed2"
        textAlign="left"
      >
        {recommendedThingsToDo()}
      </TextV2>
    </LayoutAddComponentListItem>
  );
};
